import {
    PROFILE_STATUS_CHURNED,
    PROFILE_STATUS_DISABLED,
    PROFILE_STATUS_DISABLED_BAD_PROFILE,
    PROFILE_STATUS_ENABLED,
    PROFILE_STATUS_GDPR,
    PROFILE_STATUS_GHOST,
    PROFILE_STATUS_NEW_IMPORT,
    PROFILE_STATUS_ON_HOLD,
    PROFILE_STATUS_UNTOUCHED,
} from '@advanza/constants'

// Subscription status options for dropdowns
export const SUBSCRIPTION_STATUS_OPTIONS = [
    { value: '', name: 'All Statuses' },
    { value: 'active', name: 'Active' },
    { value: 'inactive', name: 'Inactive' },
    { value: 'pending', name: 'Pending' },
]

// Provider status configuration - single source of truth
const PROVIDER_STATUS_CONFIG = [
    { code: PROFILE_STATUS_ENABLED, key: 'ENABLED', displayText: 'Active' },
    { code: PROFILE_STATUS_UNTOUCHED, key: 'UNTOUCHED', displayText: 'Basic' },
    { code: PROFILE_STATUS_DISABLED, key: 'DISABLED', displayText: 'Disabled' },
    { code: PROFILE_STATUS_ON_HOLD, key: 'ON_HOLD', displayText: 'Paused' },
    { code: PROFILE_STATUS_GDPR, key: 'GDPR', displayText: 'GDPR' },
    {
        code: PROFILE_STATUS_DISABLED_BAD_PROFILE,
        key: 'DISABLED_BAD_PROFILE',
        displayText: 'Bad Profile',
    },
    { code: PROFILE_STATUS_GHOST, key: 'GHOST', displayText: 'Ghost' },
    { code: PROFILE_STATUS_NEW_IMPORT, key: 'NEW_IMPORT', displayText: 'New Import' },
    { code: PROFILE_STATUS_CHURNED, key: 'CHURNED', displayText: 'Churned' },
] as const

// Provider status codes (from backend)
export const PROVIDER_STATUS_CODES = PROVIDER_STATUS_CONFIG.reduce(
    (acc, { code, key }) => ({ ...acc, [key]: code }),
    {} as Record<string, number>,
)

// Provider status options for dropdowns
export const PROVIDER_STATUS_OPTIONS = [
    { value: '', name: 'All Provider Statuses' },
    ...PROVIDER_STATUS_CONFIG.map(({ code, displayText }) => ({ value: code, name: displayText })),
]

// Provider status code to display text mapping
export const PROVIDER_STATUS_DISPLAY_MAP: Record<number, string> = PROVIDER_STATUS_CONFIG.reduce(
    (acc, { code, displayText }) => ({ ...acc, [code]: displayText }),
    {},
)

// Provider status color mapping (by display text)
export const PROVIDER_STATUS_COLORS: Record<string, string> = {
    Active: 'text-active-green',
    Paused: 'text-paused-orange',
    Disabled: 'text-disabled-pink',
    Churned: 'text-churned-red',
    Basic: 'text-disabled-pink',
    GDPR: 'text-purple',
    'Bad Profile': 'text-bad-profile-grey',
    Ghost: 'text-disabled-pink',
    'New Import': 'text-paused-orange',
}

// Provider status icon mapping
export const PROVIDER_STATUS_ICONS: Record<string, string> = {
    Active: 'power',
    Paused: 'pause_circle',
    Disabled: 'power_off',
    Churned: 'stop_circle',
    Basic: 'thumb_down_alt',
    GDPR: 'delete_forever',
    'Bad Profile': 'thumb_down_alt',
    Ghost: '👻',
    'New Import': 'fiber_new',
}

// Helper function to convert status code to display text
export const getProviderStatusDisplayText = (statusCode: number): string => {
    return PROVIDER_STATUS_DISPLAY_MAP[statusCode] || 'Unknown'
}

// Churn reason codes to display text mapping (from PremiumCancelRequestsTable::CANCEL_REASONS)
export const CHURN_REASON_DISPLAY_MAP: Record<string, string> = {
    '1': 'Too busy to receive leads',
    '2': 'Unable to reach the leads',
    '3': 'Lead requests do not match my services',
    '4': 'Not converting enough leads into customers',
    '5': 'The lead costs are too high',
    '6': 'Receiving leads outside of my service area',
    '7': 'Other marketing channels work better',
    '8': 'Disagree with the ranking in Top 10',
    '9': 'Company is closing down',
    '10': 'Other',
    '11': 'Unsatisfied with the quality of lead requests',
    '12': 'Declined win-back discount offer',
    '13': 'Declined to pay monthly Premium contribution',
    '14': 'Subscription price is too high',
    '101': 'Unsubscribed by Trustoo/Trustlocal',
    '102': 'Bad debt / Late payment',
    '103': 'Quality of company insufficient (does not belong on platform)',
    '104': 'Unreachable customer for long period (e.g. untouched)',
}

// Helper function to convert churn reason code to display text
export const getChurnReasonDisplayText = (reasonCode: string | number): string => {
    const code = String(reasonCode)
    return CHURN_REASON_DISPLAY_MAP[code] || 'Unknown reason'
}

// Churn reasons a provider can pick in the cancellation flow (mirrors the
// ChurnReasonModal options), used for the win-back report churn-reason filter.
const CHURN_FILTER_REASON_CODES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 13, 14, 10]

export const CHURN_REASON_OPTIONS = [
    { value: '', name: 'All Churn Reasons' },
    ...CHURN_FILTER_REASON_CODES.map((code) => ({
        value: code,
        name: CHURN_REASON_DISPLAY_MAP[String(code)],
    })),
]

// Subscription status styling
export const SUBSCRIPTION_STATUS_STYLES = {
    active: 'bg-green-200 text-green-800',
    inactive: 'bg-neutral-100 text-neutral-800',
    pending: 'bg-yellow-100 text-yellow-800',
}
